import { useCallback, useState, createContext, useContext, useMemo } from 'react';
import { ContextData, ContextProviderProps } from 'types/context';
import { ListDataCertifications } from '../api/data-certifications-endpoint';
import { DataCertificationIdentifier } from '../types/data-certification';

const DataCertificationsContext = createContext<ContextData<DataCertificationIdentifier> | undefined>(undefined);
DataCertificationsContext.displayName = 'dataCertificationsContext';

function useDataCertificationContextData(): ContextData<DataCertificationIdentifier> {
  const [dataCertifications, setDataCertifications] = useState<DataCertificationIdentifier[]>([]);
  const [filteredDataCertifications, setFilteredDataCertifications] = useState<DataCertificationIdentifier[] | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);

  const fetchDataCertifications = useCallback(async () => {
    setIsLoading(true);
    const listDataCertificationsResponse = await ListDataCertifications();
    if (listDataCertificationsResponse.success && listDataCertificationsResponse.data) {
      setDataCertifications(listDataCertificationsResponse.data);
    } else {
      setDataCertifications([]);
    }
    setIsLoading(false);
  }, [setDataCertifications]);

  const filterDataCertifications = useCallback(
    async (filter: string) => {
      if (filter === '') {
        setFilteredDataCertifications(undefined);
        return;
      }

      const filterResult = dataCertifications.filter((dataCertification) => {
        return dataCertification.Id.toLowerCase().includes(filter.toLowerCase()) || dataCertification.DistrictId.toLowerCase().includes(filter.toLowerCase());
      });

      setFilteredDataCertifications(filterResult);
    },
    [setFilteredDataCertifications, dataCertifications],
  );

  return useMemo(
    () => ({
      data: filteredDataCertifications ?? dataCertifications,
      isLoading,
      fetchData: fetchDataCertifications,
      filterData: filterDataCertifications,
    }),
    [dataCertifications, filteredDataCertifications, isLoading, fetchDataCertifications, filterDataCertifications],
  );
}

export const useDataCertificationsContext = (): ContextData<DataCertificationIdentifier> => {
  const dataCertificationsContext = useContext(DataCertificationsContext);
  if (!dataCertificationsContext) {
    throw new Error('useDataCertificationsContext must be used within the DataCertificationsContext.Provider');
  }
  return dataCertificationsContext;
};

export function DataCertificationsContextProvider({ children }: ContextProviderProps): JSX.Element {
  const dataCertificationContextData = useDataCertificationContextData();
  return <DataCertificationsContext.Provider value={dataCertificationContextData}>{children}</DataCertificationsContext.Provider>;
}

export default {
  useDataCertificationsContext,
  DataCertificationsContextProvider,
};
